import {
  Box,
  Typography,
  LinearProgress,
  Button,
} from "@mui/material";

import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import SatelliteAltIcon from "@mui/icons-material/SatelliteAlt";
import CoronavirusIcon from "@mui/icons-material/Coronavirus";
import DatasetIcon from "@mui/icons-material/Dataset";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import InsightsIcon from "@mui/icons-material/Insights";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import HubIcon from "@mui/icons-material/Hub";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";

import { useTranslate } from "../../components/Translate/TranslateContext";

const PIPELINE_ORDER = [
  "weather_data",
  "satellite",
  "disease",
  "dataprep",
  "train",
  "predict",
  "applied_metrics",
  "irrigation",
  "microclimate",
];

const STEP_META = {
  weather_data: {
    label: "pipeline_load_weather_data",
    icon: CloudQueueIcon,
  },
  satellite: {
    label: "pipeline_process_satellite_data",
    icon: SatelliteAltIcon,
  },
  disease: {
    label: "pipeline_calculate_disease_risk",
    icon: CoronavirusIcon,
  },
  dataprep: {
    label: "pipeline_prepare_farm_data",
    icon: DatasetIcon,
  },
  train: {
    label: "pipeline_train_models",
    icon: ModelTrainingIcon,
  },
  predict: {
    label: "pipeline_run_predictions",
    icon: InsightsIcon,
  },
  applied_metrics: {
    label: "pipeline_calculate_daily_values",
    icon: AnalyticsIcon,
  },
  irrigation: {
    label: "pipeline_build_irrigation_schedule",
    icon: WaterDropIcon,
  },
  microclimate: {
    label: "pipeline_analyze_field_climates",
    icon: HubIcon,
  },
};

const normalizeStatus = (status) => {
  if (
    status === "success" ||
    status === "completed" ||
    status === "finished"
  ) {
    return "success";
  }

  if (status === "failed") {
    return "failed";
  }

  if (status === "running") {
    return "running";
  }

  if (status === "paused") {
    return "paused";
  }

  return "pending";
};

export default function DataSetup({
  run,
  onRetry,
}) {
  const { translate } =
    useTranslate();

  if (!run) {
    return null;
  }

  const pipelineOrder =
    run.pipeline_order?.length
      ? run.pipeline_order
      : PIPELINE_ORDER;

  const steps =
    run.steps ?? [];

  const step =
    run.step ?? {};

  const currentStep =
    step.step_name ?? null;

  const currentStatus =
    normalizeStatus(
      step.status
    );

  const stepsByName =
    steps.reduce(
      (acc, item) => {
        acc[item.step_name] =
          item;
        return acc;
      },
      {}
    );

  const currentIndex =
    pipelineOrder.indexOf(
      currentStep
    );

  const resolveStepStatus = (
    stepKey
  ) => {
    const savedStep =
      stepsByName[stepKey];

    if (savedStep?.status) {
      return normalizeStatus(
        savedStep.status
      );
    }

    if (
      stepKey === currentStep
    ) {
      return currentStatus;
    }

    const stepIndex =
      pipelineOrder.indexOf(
        stepKey
      );

    if (
      run.finished
    ) {
      return "success";
    }

    if (
      currentIndex !== -1 &&
      stepIndex < currentIndex
    ) {
      return "success";
    }

    return "pending";
  };

  const getProgress =
    () => {
      if (run.finished) {
        return 100;
      }

      const completed =
        pipelineOrder.filter(
          (stepKey) =>
            resolveStepStatus(
              stepKey
            ) === "success"
        ).length;

      const running =
        pipelineOrder.some(
          (stepKey) =>
            resolveStepStatus(
              stepKey
            ) === "running"
        );

      const base =
        (completed /
          pipelineOrder.length) *
        100;

      return running
        ? Math.min(base + 8, 98)
        : base;
    };

  const renderStatusIcon = (
    status
  ) => {
    if (
      status === "success"
    ) {
      return (
        <CheckCircleRoundedIcon
          sx={{
            color:
              "var(--color-green-main)",
          }}
        />
      );
    }

    if (
      status === "failed"
    ) {
      return (
        <ErrorRoundedIcon
          color="error"
        />
      );
    }

    return (
      <RadioButtonUncheckedRoundedIcon
        sx={{
          color:
            status === "running"
              ? "var(--color-green-main)"
              : "#B9B9B9",
        }}
      />
    );
  };

  const hasFailedStep =
    pipelineOrder.some(
      (stepKey) =>
        resolveStepStatus(
          stepKey
        ) === "failed"
    );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: 760,
        mx: "auto",
        py: 5,
        px: {
          xs: 3,
          md: 0,
        },
      }}
    >
      <Box
        sx={{
          textAlign:
            "center",
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontFamily:
              "Fraunces",
            fontSize: {
              xs: 38,
              md: 54,
            },
            color:
              "var(--color-text-primary)",
            lineHeight: 1.1,
          }}
        >
          {translate(
            "setting_up_system"
          )}
        </Typography>
      </Box>

      <Box
        sx={{
          p: 4,
          borderRadius:
            "28px",
          background:
            "rgba(212,233,207,0.22)",
          border:
            "1px solid rgba(5,40,18,0.08)",
        }}
      >
        <LinearProgress
          variant="determinate"
          value={getProgress()}
          sx={{
            height: 12,
            borderRadius: 999,
            mb: 4,
            backgroundColor:
              "rgba(36,88,42,0.12)",
            "& .MuiLinearProgress-bar":
              {
                backgroundColor:
                  "var(--color-green-main)",
              },
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection:
              "column",
            gap: 1.5,
          }}
        >
          {pipelineOrder.map(
            (
              stepKey,
              index
            ) => {
              const meta =
                STEP_META[stepKey] ??
                STEP_META.dataprep;

              const status =
                resolveStepStatus(
                  stepKey
                );

              const Icon =
                meta.icon;

              const active =
                status === "running";

              return (
                <Box
                  key={stepKey}
                  sx={{
                    display:
                      "flex",
                    alignItems:
                      "center",
                    justifyContent:
                      "space-between",
                    gap: 2,
                    p: 2,
                    borderRadius: 4,
                    backgroundColor:
                      active
                        ? "rgba(212,233,207,0.6)"
                        : "transparent",
                  }}
                >
                  <Box
                    sx={{
                      display:
                        "flex",
                      alignItems:
                        "center",
                      gap: 2,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius:
                          "50%",
                        backgroundColor:
                          "rgba(36,88,42,0.12)",
                        display:
                          "flex",
                        alignItems:
                          "center",
                        justifyContent:
                          "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon />
                    </Box>

                    <Box
                      sx={{
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color:
                            "var(--color-text-primary)",
                        }}
                      >
                        {index + 1}.{" "}
                        {translate(
                          meta.label
                        )}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color:
                            status ===
                            "failed"
                              ? "error.main"
                              : "text.secondary",
                        }}
                      >
                        {translate(
                          `pipeline_status_${status}`
                        )}
                      </Typography>
                    </Box>
                  </Box>

                  {renderStatusIcon(
                    status
                  )}
                </Box>
              );
            }
          )}
        </Box>

        {hasFailedStep &&
          onRetry && (
            <Box
              sx={{
                mt: 3,
                display: "flex",
                justifyContent:
                  "center",
              }}
            >
              <Button
                variant="contained"
                onClick={onRetry}
              >
                {translate("retry")}
              </Button>
            </Box>
          )}
      </Box>
    </Box>
  );
}